import { motion } from "framer-motion";
import { Globe, Smartphone, Cpu, Palette, TrendingUp, Sparkles } from "lucide-react";

const iconMap = {
  Globe: Globe,
  Smartphone: Smartphone,
  Cpu: Cpu,
  Palette: Palette,
  TrendingUp: TrendingUp,
};

const ServicesSection = ({ services }) => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  const serviceDetails = {
    web: {
      benefits: ["Sitios ultra rápidos", "SEO optimizado", "Conversión maximizada"],
      gradient: "from-blue-500/20 to-cyan-500/20",
    },
    mobile: {
      benefits: ["iOS y Android", "UI/UX premium", "Alto rendimiento"],
      gradient: "from-purple-500/20 to-pink-500/20",
    },
    saas: {
      benefits: ["Automatización IA", "Escalable", "Cloud-native"],
      gradient: "from-emerald-500/20 to-teal-500/20",
    },
    design: {
      benefits: ["Identidad única", "Guías de marca", "Assets completos"],
      gradient: "from-orange-500/20 to-amber-500/20",
    },
    marketing: {
      benefits: ["ROI medible", "Estrategia data-driven", "Crecimiento sostenido"],
      gradient: "from-rose-500/20 to-red-500/20",
    },
  };

  return (
    <section
      id="servicios"
      className="py-24 md:py-32 bg-[#030303] relative"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#00E5FF] mb-4 block">
            Servicios
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white mb-4">
            Soluciones digitales de{" "}
            <span className="gradient-text">alto impacto</span>
          </h2>
          <p className="text-zinc-400 max-w-2xl mx-auto text-lg">
            Cada servicio diseñado para generar resultados medibles y escalables
          </p>
        </motion.div>

        {/* Bento Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          className="grid grid-cols-1 md:grid-cols-12 gap-6"
        >
          {services.map((service, index) => {
            const Icon = iconMap[service.icon] || Sparkles;
            const details = serviceDetails[service.id] || {};
            
            // Bento grid layout: first 2 take more space
            const gridClass =
              index === 0
                ? "md:col-span-7"
                : index === 1
                ? "md:col-span-5"
                : "md:col-span-4";

            return (
              <motion.div
                key={service.id}
                variants={itemVariants}
                className={`${gridClass} service-card bg-[#0A0A0A] border border-white/5 rounded-3xl p-8 group cursor-pointer`}
                data-testid={`service-card-${service.id}`}
              >
                {/* Icon */}
                <div
                  className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${
                    details.gradient || "from-blue-500/20 to-cyan-500/20"
                  } flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}
                >
                  <Icon className="w-7 h-7 text-white" />
                </div>

                {/* Title */}
                <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-[#00E5FF] transition-colors">
                  {service.title}
                </h3>

                {/* Description */}
                <p className="text-zinc-400 mb-6 leading-relaxed">
                  {service.description}
                </p>

                {/* Benefits */}
                {details.benefits && (
                  <div className="flex flex-wrap gap-2">
                    {details.benefits.map((benefit, i) => (
                      <span
                        key={i}
                        className="text-xs px-3 py-1 rounded-full bg-white/5 text-zinc-400 border border-white/5"
                      >
                        {benefit}
                      </span>
                    ))}
                  </div>
                )}
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSection;
